import { useTranslation } from 'react-i18next';
import type { CatalogMeta } from '@/lib/catalog';

export interface CatalogFilterState {
  source: 'All' | 'Own' | 'Partner';
  partnerId: string;
  category: string;
  inStock: boolean;
  maxPrice: number;
  color: string;
  base: string;
  query: string;
}

interface Props {
  value: CatalogFilterState;
  onChange: (next: CatalogFilterState) => void;
  activeCount: number;
  onReset: () => void;
  meta: CatalogMeta | null;
  priceCap: number;
}

const baseLabels: Record<string, { en: string; ru: string }> = {
  Chrome: { en: 'Chrome', ru: 'Хром' },
  Steel: { en: 'Steel', ru: 'Сталь' },
  Panel: { en: 'Panel', ru: 'Панель' },
  Timber: { en: 'Timber', ru: 'Дерево' },
  Aluminium: { en: 'Aluminium', ru: 'Алюминий' },
  'Black steel': { en: 'Black steel', ru: 'Чёрная сталь' },
  'Black nylon': { en: 'Black nylon', ru: 'Чёрный нейлон' },
  'White nylon': { en: 'White nylon', ru: 'Белый нейлон' },
};

const colorLabels: Record<string, { en: string; ru: string }> = {
  Black: { en: 'Black', ru: 'Чёрный' },
  Grey: { en: 'Grey', ru: 'Серый' },
  Blue: { en: 'Blue', ru: 'Синий' },
  White: { en: 'White', ru: 'Белый' },
  Chrome: { en: 'Chrome', ru: 'Хром' },
  Walnut: { en: 'Walnut', ru: 'Орех' },
  Oak: { en: 'Oak', ru: 'Дуб' },
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-background-200 px-3.5 py-3 first:border-0">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-500">{title}</p>
      {children}
    </div>
  );
}

/** Фильтры каталога: справочники (партнёры, категории, цвета, основания, потолок цены) приходят с сервера (/catalog/meta). */
export default function CatalogFilters({ value, onChange, activeCount, onReset, meta, priceCap }: Props) {
  const { t, i18n } = useTranslation();
  const ru = i18n.language === 'ru';
  const set = (patch: Partial<CatalogFilterState>) => onChange({ ...value, ...patch });
  const label = (map: Record<string, { en: string; ru: string }>, key: string) => (map[key] ? (ru ? map[key].ru : map[key].en) : key);
  const catLabel = (code: string) => t(`category.${code}`, { defaultValue: code });

  return (
    <aside className="w-full shrink-0 lg:w-[240px]">
      <div className="sticky top-[72px] overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between px-3.5 py-2.5">
          <div className="flex items-center gap-1.5">
            <i className="ri-filter-3-line text-[13px] text-foreground-500" />
            <p className="text-[12px] font-semibold text-foreground-950">{t('catalog.filters')}</p>
            {activeCount > 0 && <span className="tnum rounded-full bg-primary-50 px-1.5 text-[10px] font-semibold text-primary-800">{activeCount}</span>}
          </div>
          <button type="button" onClick={onReset} className="cursor-pointer text-[10.5px] font-medium text-foreground-500 transition-colors hover:text-primary-700">
            {t('common.reset')}
          </button>
        </div>

        <Section title={t('catalog.search')}>
          <div className="relative">
            <i className="ri-search-line pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[12px] text-foreground-400" />
            <input
              value={value.query}
              onChange={(e) => set({ query: e.target.value })}
              placeholder={t('catalog.searchPlaceholder')}
              aria-label={t('catalog.searchPlaceholder')}
              className="h-7 w-full rounded border border-background-200 bg-background-100 pl-7 pr-2 text-[11.5px] outline-none focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </Section>

        <Section title={t('catalog.source')}>
          <div className="flex gap-1 rounded border border-background-200 bg-background-100 p-0.5">
            {(['All', 'Own', 'Partner'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => set({ source: s, partnerId: s === 'Own' ? 'All' : value.partnerId })}
                className={`flex-1 cursor-pointer whitespace-nowrap rounded px-1 py-1 text-[11px] font-medium transition-colors ${value.source === s ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'}`}
              >
                {s === 'All' ? t('common.all') : s === 'Own' ? t('catalog.own') : t('nav.partners')}
              </button>
            ))}
          </div>
        </Section>

        <Section title={t('catalog.partner')}>
          <select
            value={value.partnerId}
            onChange={(e) => set({ partnerId: e.target.value, source: e.target.value === 'All' ? value.source : 'Partner' })}
            aria-label={t('catalog.partner')}
            className="h-7 w-full cursor-pointer rounded border border-background-200 bg-background-50 px-1.5 text-[11.5px] text-foreground-700 outline-none focus:border-primary-400"
          >
            <option value="All">{t('catalog.allPartners')}</option>
            {(meta?.partners ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.shortName}
              </option>
            ))}
          </select>
        </Section>

        <Section title={t('catalog.category')}>
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => set({ category: 'All' })}
              className={`cursor-pointer rounded px-1.5 py-1 text-left text-[11.5px] transition-colors ${value.category === 'All' ? 'bg-primary-50 font-medium text-primary-800' : 'text-foreground-600 hover:bg-background-100'}`}
            >
              {t('catalog.allCategories')}
            </button>
            {(meta?.categories ?? []).map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => set({ category: c.code })}
                className={`flex cursor-pointer items-center justify-between gap-2 rounded px-1.5 py-1 text-left text-[11.5px] transition-colors ${value.category === c.code ? 'bg-primary-50 font-medium text-primary-800' : 'text-foreground-600 hover:bg-background-100'}`}
              >
                <span className="truncate">{catLabel(c.code)}</span>
                <span className="tnum text-[10px] text-foreground-400">{c.count}</span>
              </button>
            ))}
          </div>
        </Section>

        <Section title={t('catalog.availability')}>
          <label className="flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={value.inStock} onChange={(e) => set({ inStock: e.target.checked })} className="h-3.5 w-3.5 cursor-pointer accent-[oklch(var(--primary-500))]" />
            <span className="text-[11.5px] text-foreground-700">{t('catalog.inStockOnly')}</span>
          </label>
          <p className="mt-1 pl-5 text-[10.5px] leading-4 text-foreground-400">{t('catalog.inStockHint')}</p>
        </Section>

        <Section title={t('catalog.priceRange')}>
          <input type="range" min={150} max={priceCap} step={25} value={Math.min(value.maxPrice, priceCap)} onChange={(e) => set({ maxPrice: Number(e.target.value) })} aria-label={t('catalog.priceRange')} className="h-1 w-full cursor-pointer" />
          <div className="mt-1.5 flex items-center justify-between text-[10.5px] text-foreground-500">
            <span>AED 150</span>
            <span className="tnum font-semibold text-foreground-900">≤ {value.maxPrice}</span>
          </div>
        </Section>

        {(meta?.colors.length ?? 0) > 0 && (
          <Section title={t('catalog.colour')}>
            <div className="flex flex-wrap gap-1">
              {['All', ...(meta?.colors ?? [])].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set({ color: c })}
                  className={`cursor-pointer whitespace-nowrap rounded border px-1.5 py-0.5 text-[10.5px] transition-colors ${value.color === c ? 'border-primary-300 bg-primary-50 font-medium text-primary-800' : 'border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100'}`}
                >
                  {c === 'All' ? t('common.all') : label(colorLabels, c)}
                </button>
              ))}
            </div>
          </Section>
        )}

        {(meta?.bases.length ?? 0) > 0 && (
          <Section title={t('catalog.baseType')}>
            <div className="flex flex-wrap gap-1">
              {['All', ...(meta?.bases ?? [])].map((b) => (
                <button
                  key={b}
                  type="button"
                  onClick={() => set({ base: b })}
                  className={`cursor-pointer whitespace-nowrap rounded border px-1.5 py-0.5 text-[10.5px] transition-colors ${value.base === b ? 'border-primary-300 bg-primary-50 font-medium text-primary-800' : 'border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100'}`}
                >
                  {b === 'All' ? t('common.all') : label(baseLabels, b)}
                </button>
              ))}
            </div>
          </Section>
        )}
      </div>
    </aside>
  );
}
